"use client";

import { useFormContext } from "react-hook-form";
import { CardContent } from "@/components/ui/card";
import {
  FormField,
  FormItem,
  FormLabel,
  FormControl,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { ImageUpload } from "@/components/ImageUpload";
import MultiTagSelector from "@/components/MultiTagSelector";
import CurrencyInput from "@/components/CurrencyInput";
import { RichTextEditor } from "@/components/RichTextEditor";
import { numberToMoney } from "@/util/moneyConversion";
import {
  Accordion,
  AccordionItem,
  AccordionTrigger,
  AccordionContent,
} from "@/components/ui/accordion";

export default function BasicInfoStep({
  initialImage,
  onImageChange,
}: {
  initialImage?: string;
  onImageChange?: (file: File | null) => void;
}) {
  const { control, setValue, watch } = useFormContext();

  const unitPrice = watch("unitPrice") || 0;
  const unitCost = watch("unitCost") || 0;
  const marginValue = unitPrice - unitCost;
  const marginPercentage = unitPrice > 0 ? (marginValue / unitPrice) * 100 : 0;

  return (
    <CardContent className="p-4 sm:p-6 space-y-4">
      <div className="grid grid-cols-1 lg:grid-cols-4 gap-4">
        {/* Coluna A - Imagem do Produto */}
        <div className="lg:col-span-2 space-y-4">
          <FormField
            control={control}
            name="imageUrl"
            render={({ field }) => (
              <FormItem>
                <FormLabel className="text-sm font-medium">
                  Imagem do Produto
                </FormLabel>
                <FormControl>
                  <div className="w-full max-w-sm mx-auto lg:mx-0">
                    <ImageUpload
                      initialImage={initialImage || field.value}
                      onImageChange={(file) => {
                        if (onImageChange) {
                          onImageChange(file);
                        }

                        if (file) {
                          const tempUrl = URL.createObjectURL(file);
                          setValue("imageUrl", tempUrl, {
                            shouldValidate: true,
                          });
                        } else {
                          setValue("imageUrl", undefined, {
                            shouldValidate: true,
                          });
                        }
                      }}
                    />
                  </div>
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>

        {/* Coluna B - Nome do Produto (largura completa na parte direita) */}
        <div className="lg:col-span-2 space-y-4">
          <FormField
            control={control}
            name="name"
            render={({ field }) => (
              <FormItem>
                <FormLabel className="text-sm font-medium">
                  Nome do Produto
                </FormLabel>
                <FormControl>
                  <Input
                    {...field}
                    type="text"
                    className="h-12 text-base"
                    placeholder="Digite o nome do produto"
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          {/* Tags */}
          <FormField
            control={control}
            name="tags"
            render={({ field }) => (
              <FormItem>
                <FormLabel className="text-sm font-medium">
                  Tags do Produto
                </FormLabel>
                <FormControl>
                  <MultiTagSelector
                    value={field.value}
                    onChange={field.onChange}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>

        {/* Coluna C - Preços & Custos (largura completa) */}
        <div className="lg:col-span-4">
          <div className="space-y-2">
            <FormLabel className="text-sm font-medium">
              Preços &amp; Custos
            </FormLabel>
            <Accordion
              type="single"
              collapsible
              defaultValue="precos"
              className="w-full"
            >
              <AccordionItem value="precos" className="border rounded-lg">
                <AccordionTrigger className="px-3 py-2.5 text-sm hover:no-underline min-h-[48px]">
                  <div className="flex items-center justify-between w-full mr-2">
                    <span className="text-left">
                      Configurar valores financeiros
                    </span>
                    {(unitPrice > 0 || unitCost > 0) && (
                      <span className="text-xs bg-muted px-2 py-1 rounded text-muted-foreground">
                        {unitPrice > 0 ? numberToMoney(unitPrice) : "R$ 0,00"} /{" "}
                        {unitCost > 0 ? numberToMoney(unitCost) : "R$ 0,00"}
                      </span>
                    )}
                  </div>
                </AccordionTrigger>
                <AccordionContent className="px-3 pb-4 space-y-4">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <FormField
                      control={control}
                      name="unitPrice"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel className="text-sm font-medium">
                            Preço de Venda
                          </FormLabel>
                          <FormControl>
                            <CurrencyInput
                              {...field}
                              className="w-full h-12 text-base"
                              placeholder="0,00"
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={control}
                      name="unitCost"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel className="text-sm font-medium">
                            Custo do Produto
                          </FormLabel>
                          <FormControl>
                            <CurrencyInput
                              {...field}
                              className="w-full h-12 text-base"
                              placeholder="0,00"
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>

                  {/* Análise Financeira */}
                  {unitPrice > 0 && unitCost > 0 && (
                    <div className="p-4 bg-gradient-to-r from-blue-50 to-green-50 rounded-lg border border-blue-200">
                      <div className="flex items-center justify-between">
                        <div>
                          <h4 className="text-sm font-medium text-gray-900 mb-1">
                            Análise Financeira
                          </h4>
                          <div className="text-sm text-gray-600">
                            Margem de lucro:{" "}
                            <span
                              className={`font-semibold ${
                                marginValue >= 0
                                  ? "text-green-600"
                                  : "text-red-600"
                              }`}
                            >
                              {numberToMoney(marginValue)}
                            </span>
                          </div>
                        </div>
                        <div className="text-right">
                          <div
                            className={`text-lg font-bold ${
                              marginValue >= 0
                                ? "text-green-600"
                                : "text-red-600"
                            }`}
                          >
                            {marginPercentage.toFixed(1)}%
                          </div>
                          <div className="text-xs text-gray-500">Margem</div>
                        </div>
                      </div>
                    </div>
                  )}

                  {(unitPrice === 0 || unitCost === 0) && (
                    <div className="p-3 bg-muted/30 rounded-lg border">
                      <div className="text-sm text-muted-foreground text-center py-1">
                        Defina preço e custo para calcular a margem
                      </div>
                    </div>
                  )}
                </AccordionContent>
              </AccordionItem>
            </Accordion>
          </div>
        </div>
      </div>

      {/* Descrição - Largura completa para mais espaço horizontal */}
      <div className="space-y-2">
        <FormLabel className="text-sm font-medium">Descrição</FormLabel>
        <Accordion
          type="single"
          collapsible
          defaultValue="descricao"
          className="w-full"
        >
          <AccordionItem value="descricao" className="border rounded-lg">
            <AccordionTrigger className="px-3 py-2.5 text-sm hover:no-underline min-h-[48px]">
              <div className="flex items-center justify-between w-full mr-2">
                <span className="text-left">Adicionar descrição detalhada</span>
              </div>
            </AccordionTrigger>
            <AccordionContent className="px-3 pb-4">
              <FormField
                control={control}
                name="description"
                render={({ field }) => (
                  <FormItem>
                    <FormControl>
                      <RichTextEditor
                        value={field.value || ""}
                        onChange={field.onChange}
                        placeholder="Detalhes do produto..."
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </AccordionContent>
          </AccordionItem>
        </Accordion>
      </div>
    </CardContent>
  );
}
